import type { paths } from '@/utils/core-api/core-api.auto-generated';
import { Environment } from '@/utils/environment/environment.interface';
import { interpolateString } from '@/utils/toolkit/string/interpolate-string.utils';
import type { Branded } from '@/utils/typing/branded.type';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import type {
    FilterKeys,
    IsOperationRequestBodyOptional,
    OperationRequestBodyContent,
    PathsWithMethod,
    SuccessResponse
} from 'openapi-typescript-helpers';
import { BehaviorSubject, firstValueFrom, type Observable, of } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import type { CoreApiToken } from '../../shared';
import { RosaAuthStoreActions, RosaAuthStoreSelectors } from '../store/auth';
import type { SessionId } from './auth/auth.interface';

@Injectable()
export class RosaCoreApiService {
    http = inject(HttpClient);
    private _store = inject<Store<any>>(Store);
    private _env = inject(Environment);

    private _isRefreshingToken: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private _coreApiUrl = this.defineCoreApiUrl();

    private defineCoreApiUrl() {
        const url = new URL(this._env.coreApiEndPoint, window.location.origin).href;
        return url.endsWith('/') ? url : url + '/';
    }

    private getOptions$(headers?: any, params?: any, noAuth?: boolean): Observable<object> {
        headers = headers || {};
        headers.Branding = window.location.hostname;

        if (!noAuth) {
            return this.getToken$().pipe(
                map(token => {
                    headers.Authorization = `Bearer ${token.access_token}`;
                    return {
                        headers: new HttpHeaders(headers),
                        params,
                        withCredentials: true
                    };
                })
            );
        } else {
            return of({
                headers: new HttpHeaders(headers),
                params,
                withCredentials: true
            });
        }
    }

    getToken$(): Observable<CoreApiToken> {
        return this._store.pipe(select(RosaAuthStoreSelectors.selectToken)).pipe(
            filter(isNotNullish),
            take(1),
            mergeMap(token => {
                if (token.expires_at <= moment().valueOf()) {
                    if (!this._isRefreshingToken.value) {
                        if (!token.refresh_token) {
                            throw new Error('NO_REFRESH_TOKEN_IS_STORED');
                        }
                        this._isRefreshingToken.next(true);
                        firstValueFrom(this.getNewTokenFromRefresh$(token.refresh_token)).then(refreshedToken => {
                            this.storeToken(refreshedToken);
                            this._isRefreshingToken.next(false);
                        });
                    }
                    return this._isRefreshingToken.pipe(
                        filter(isRefreshingToken => isRefreshingToken === false),
                        mergeMap(() => this._store.pipe(select(RosaAuthStoreSelectors.selectToken))),
                        filter(isNotNullish),
                        take(1)
                    );
                } else {
                    return of(token);
                }
            })
        );
    }

    storeToken(token: CoreApiToken, newSessionId?: SessionId): void {
        this._store.dispatch(new RosaAuthStoreActions.UpdateOauthToken({ token, newSessionId }));
    }

    getNewTokenFromRefresh$(refreshToken: string): Observable<CoreApiToken> {
        const basicAuth = this._env.advizeoClientId + ':' + this._env.advizeoClientSecret;

        const decodedToken = jwtDecode<{ project_id: string }>(refreshToken);

        const formData = new FormData();
        formData.append('refresh_token', refreshToken);
        formData.append('grant_type', 'refresh_token');
        formData.append('project_id', decodedToken.project_id);

        return this.post<CoreApiToken>(
            'oauth/token',
            formData,
            {
                Authorization: `Basic ${btoa(basicAuth)}`,
                ...{ Branding: window.location.hostname }
            },
            true
        ).pipe(
            map(token => {
                token.expires_at = moment()
                    .add(token.expires_in - 10000, 'ms')
                    .valueOf();
                return token;
            })
        );
    }

    get<T>(route: string, params?: any, headers?: any, noAuth?: boolean): Observable<T> {
        return this.getOptions$(headers, params, noAuth).pipe(
            mergeMap(options => {
                return this.http.get<T>(this._coreApiUrl + route, options);
            })
        );
    }

    checkedGet<Path extends PathsWithMethod<paths, 'get'>>(
        url: Path,
        routeParameters: RequestOptions<FilterKeys<paths[Path], 'get'>>,
        noAuth?: boolean
    ): Observable<MakeBranded<SuccessResponse<paths[Path]['get']['responses']>>> {
        return this.get(this.makeUrl(url, routeParameters.path, routeParameters.query), null, null, noAuth);
    }

    getFile<T>(route: string): Observable<T> {
        return this.getOptions$().pipe(
            mergeMap(options => {
                return this.http.get<T>(this._coreApiUrl + route, {
                    ...options,
                    responseType: 'blob'
                } as object);
            })
        );
    }

    getFromRawUrl<T>(route: string, params?: any, headers?: any, noAuth?: boolean): Observable<T> {
        return this.getOptions$(headers, params, noAuth).pipe(
            mergeMap(options => {
                return this.http.get<T>(route, options);
            })
        );
    }

    post<T>(route: string, body: any, headers?: any, noAuth?: boolean): Observable<T> {
        return this.getOptions$(headers, null, noAuth).pipe(
            mergeMap(options => {
                return this.http.post<T>(this._coreApiUrl + route, body, options);
            })
        );
    }

    checkedPost<Path extends PathsWithMethod<paths, 'post'>>(
        url: Path,
        routeParameters: RequestOptions<FilterKeys<paths[Path], 'post'>>
    ): Observable<MakeBranded<SuccessResponse<paths[Path]['post']['responses']>>> {
        return this.post(this.makeUrl(url, routeParameters.path, routeParameters.query), routeParameters.body ?? {});
    }

    postToFileBody<T>(route: string, file: File, body: any, headers?: any, noAuth?: boolean): Observable<T> {
        return this.getOptions$(headers, null, noAuth).pipe(
            mergeMap(options => {
                const formData = new FormData();
                formData.append('file', file);
                const jsonBody = JSON.stringify(body);
                const blobBody = new Blob([jsonBody], {
                    type: 'application/json'
                });
                formData.append('body', blobBody);
                return this.http.post<T>(this._coreApiUrl + route, formData, options);
            })
        );
    }

    postToFile<T>(route: string, body: any, needsFullResponse: boolean = false): Observable<T> {
        return this.getOptions$().pipe(
            mergeMap(options => {
                return this.http.post<T>(this._coreApiUrl + route, body, {
                    ...options,
                    responseType: 'blob',
                    observe: needsFullResponse ? 'response' : undefined
                } as object);
            })
        );
    }

    put<T>(route: string, body: any, headers?: any, noAuth?: boolean): Observable<T> {
        return this.getOptions$(headers, null, noAuth).pipe(
            mergeMap(options => {
                return this.http.put<T>(this._coreApiUrl + route, body, options);
            })
        );
    }

    checkedPut<Path extends PathsWithMethod<paths, 'put'>>(
        url: Path,
        routeParameters: RequestOptions<FilterKeys<paths[Path], 'put'>>
    ): Observable<MakeBranded<SuccessResponse<paths[Path]['put']['responses']>>> {
        return this.put(this.makeUrl(url, routeParameters.path, routeParameters.query), routeParameters.body ?? {});
    }

    patch<T>(route: string, body: any, headers?: any, noAuth?: boolean): Observable<T> {
        return this.getOptions$(headers, null, noAuth).pipe(
            mergeMap(options => {
                return this.http.patch<T>(this._coreApiUrl + route, body, options);
            })
        );
    }

    checkedPatch<Path extends PathsWithMethod<paths, 'patch'>>(
        url: Path,
        routeParameters: RequestOptions<FilterKeys<paths[Path], 'patch'>>
    ): Observable<MakeBranded<SuccessResponse<paths[Path]['patch']['responses']>>> {
        return this.patch(this.makeUrl(url, routeParameters.path, routeParameters.query), routeParameters.body ?? {});
    }

    delete<T>(route: string, headers?: any, noAuth?: boolean): Observable<T> {
        return this.getOptions$(headers, null, noAuth).pipe(
            mergeMap(options => {
                return this.http.delete<T>(this._coreApiUrl + route, options);
            })
        );
    }

    checkedDelete<Path extends PathsWithMethod<paths, 'delete'>>(
        url: Path,
        routeParameters: RequestOptions<FilterKeys<paths[Path], 'delete'>>
    ): Observable<MakeBranded<SuccessResponse<paths[Path]['delete']['responses']>>> {
        return this.delete(this.makeUrl(url, routeParameters.path, routeParameters.query));
    }

    private makeUrl(route: string, pathParams?: Record<string, unknown>, queryParams?: Record<string, unknown>): string {
        let queryString = queryParams
            ? '?' +
              Object.entries(queryParams)
                  .filter(([_key, value]) => value !== undefined)
                  .map(([key, value]) => {
                      // because spring works this way :/
                      if (key === 'sort' && Array.isArray(value)) {
                          return value.map(v => `${key}=${v}`).join('&');
                      }

                      return `${key}=${value}`;
                  })
                  .join('&')
            : '';
        // .slice(5) is for removing '/api/' from the routes as it is already added in the env.js coreApiEndPoint
        return interpolateString(route.slice(5), pathParams, /{\s?([^{}\s]*)\s?}/g) + queryString;
    }
}

export interface BatchError<T> {
    data: T;
    error: string;
}

export function createSingleRequestFromBatch<ResourceToBatch>(
    requestFunction: (resources: ResourceToBatch[], ...params: any[]) => Promise<BatchError<ResourceToBatch>[]>
): (resource: ResourceToBatch, ...params: any[]) => Promise<void> {
    return (resource: ResourceToBatch, ...params) => {
        return requestFunction([resource], params).then(response => {
            if (!response) return; // No response (204) -> OK
            if (response) {
                // response (202) -> Error
                if (Array.isArray(response) && response[0]) {
                    throw response[0].error;
                }
                throw null;
            }
        });
    };
}

type ParamsOption<T> = T extends {
    parameters: any;
}
    ? Omit<T['parameters'], 'header' | 'cookie'>
    : {};

type RequestBodyOption<T> =
    IsOperationRequestBodyOptional<T> extends true ? { body?: OperationRequestBodyContent<T> } : { body: OperationRequestBodyContent<T> };

type RequestOptions<T> = ParamsOption<T> & RequestBodyOption<T>;

type MakeBranded<T> = T extends string | number
    ? Branded<T, any>
    : T extends Array<infer U>
      ? MakeBranded<U>[]
      : T extends object
        ? { [K in keyof T]: MakeBranded<T[K]> }
        : T;
